import { Component, OnInit, Input, Output, EventEmitter, SimpleChanges, forwardRef } from '@angular/core';
import { FormGroup, FormControl, ReactiveFormsModule } from '@angular/forms';
import { 
    IonContent, IonSpinner, IonAvatar, IonLabel, IonIcon, IonGrid, IonRow, IonCol,
    IonInfiniteScroll, IonInfiniteScrollContent
} from '@ionic/angular/standalone';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { MarvColor, MarvColorPrimary } from '@marv/types';
import { MarvMessageComponent, MarvItemComponent, MarvInputComponent, MarvButtonComponent } from '@marv/components';
import { ApiService } from '@app/services';

export class MarvListExternalSource {
    url: string;
    params?: any;
    valueField: string;
    labelField: string;
    transformItem?: (json: any) => MarvListItem;
}

export class MarvListItem {
    label: string;
    value: string|number|boolean;
    color: MarvColor = MarvColorPrimary;
    picture?: string;
    
    constructor(label: string, value: string|number|boolean, picture?: string) {
        this.label = label;
        this.value = value;
        this.picture = picture;
    }
}

@Component({
    selector: 'marv-list',
    templateUrl: './list.component.html',
    styleUrls: ['./list.component.scss'],
    imports: [
        IonContent, IonSpinner, IonAvatar, IonLabel, IonIcon, IonGrid, IonRow, IonCol,
        IonInfiniteScroll, IonInfiniteScrollContent, TranslatePipe, ReactiveFormsModule, 
        forwardRef(() => MarvMessageComponent), forwardRef(() => MarvItemComponent), forwardRef(() => MarvInputComponent), forwardRef(() => MarvButtonComponent)
    ]
})
export class MarvListComponent  implements OnInit {

    @Input({alias : 'items', required : false}) items: Array<MarvListItem> = [];
    @Input({alias : 'externalSource', required : false}) externalSource: MarvListExternalSource|undefined;
    @Input({alias : 'itemsSelected', required : false}) itemsSelected: Map<number|string|boolean, MarvListItem>;
    @Input({alias : 'maxItemsDisplayed', required : false}) maxItemsDisplayed: number = 100;
    @Input({alias : 'multiple', required : false}) multiple: boolean = false;
    @Input({alias : 'title', required : false}) title: string = '';  
    @Input({alias : 'displayItemsColors', required : false}) displayItemsColors: boolean = false;  
    @Input({alias : 'displayItemsSelected', required : false}) displayItemsSelected: boolean = false;
    @Input({alias : 'withSearch', required : false}) withSearch: boolean = true;
    @Input({alias : 'searchDefault', required : false}) searchDefault: string;
    @Output() onSelectItem = new EventEmitter<Map<number|string|boolean, MarvListItem>>();
    @Output() onSearchEnd = new EventEmitter<Array<MarvListItem>>();
    
    isFetching: boolean = false; 
    indexItem: number = 0;
    itemsToDisplay: Array<MarvListItem> = [];
    itemsDisplayed: Array<MarvListItem> = [];
    itemsSelectedCopy: Map<number|string|boolean, MarvListItem> = new Map();
    searchForm: FormGroup = new FormGroup({
        search: new FormControl('')        
    });  
    updateSearchSubscription: Subscription; 
    updateSearchTimeout: any; 
    allSelected: boolean = false;
    disabledInfiniteScroll: boolean = false;
    searchValue: string = '';
     
    constructor(
        private apiService: ApiService
    ) { }

    async ngOnInit() { 
        if(this.withSearch){
            this.updateSearchSubscription = this.searchForm.controls['search'].valueChanges.subscribe(newValue => {
                if(this.updateSearchTimeout){
                    clearTimeout(this.updateSearchTimeout);
                }
                this.updateSearchTimeout = setTimeout(async () => {
                    await this.searchList(newValue);
                }, 1000);
            });     
        }
    }
        
    ngOnDestroy() {
        if(this.updateSearchTimeout){
            clearTimeout(this.updateSearchTimeout);
        }
        if(this.updateSearchSubscription){
            this.updateSearchSubscription.unsubscribe();
        }
    }
    
    async ngOnChanges(changes: SimpleChanges) {    
            
        if(changes['itemsSelected'] && changes['itemsSelected'].currentValue){
            this.itemsSelectedCopy = new Map(this.itemsSelected);
        }
    
        if((changes['items'] && changes['items'].currentValue) 
            || (changes['externalSource'] && changes['externalSource'].currentValue && this.externalSource?.url)){

            this.itemsToDisplay = this.items;

            if(this.externalSource || this.itemsToDisplay.length > 0){               
                if(this.items && Object.keys(this.itemsSelectedCopy).length === this.items.length){
                    this.allSelected = true;
                }   
                if(!this.searchDefault || this.searchDefault === ''){
                    this.indexItem = 0;
                    this.disabledInfiniteScroll = false;
                    this.itemsDisplayed = [];
                    await this.setItemsDisplayed(true);
                }
            } 
        }
        
        if(changes['searchDefault'] && changes['searchDefault'].currentValue){
            if(this.withSearch && this.searchDefault && this.searchDefault !== ''){
                this.searchForm.controls['search'].setValue(this.searchDefault);            
                await this.searchList(this.searchDefault);
                this.onSearchEnd.emit(this.itemsDisplayed);
            }
        }
        
    }
     
    async getItemsFromExternalSource(){
        if(this.externalSource && this.externalSource.url){            
            if(!this.externalSource.params){
                this.externalSource.params = {};
            }
            
            let params = Object.assign({}, this.externalSource.params);
            
            if(this.maxItemsDisplayed > 0){
                params.limit = this.maxItemsDisplayed;
            }
            params.index = this.indexItem;           
            params.query = this.searchValue;
                     
            let response = await this.apiService.get(this.externalSource.url, params);
            let cpt = 0;
            if(response?.data?.results){        
                let mapValues: Map<string|number, boolean> = new Map<string|number, boolean>;        
                for(let d of response?.data?.results){    
                    let value = d[this.externalSource.valueField];
                    let label = d[this.externalSource.labelField];
                    if(!mapValues.has(value) && (this.displayItemsSelected || !this.itemsSelected.get(d[this.externalSource.valueField])) ){                        
                        let item: MarvListItem = {
                            label: label,
                            value: value,
                            color: MarvColorPrimary
                        };
                        if(this.externalSource.transformItem){
                            item = this.externalSource.transformItem.call(this, d);
                        }                    
                        this.itemsDisplayed.push(item);
                        mapValues.set(value, true);
                        cpt++;
                    }                                
                }
                mapValues.clear();        
            }
            
            this.indexItem += cpt;
            if(this.maxItemsDisplayed === 0 || cpt < this.maxItemsDisplayed){
                this.disabledInfiniteScroll = true;
            }
        }
    }

    async setItemsDisplayed(init: boolean = false){
        
        if(init){
            this.isFetching = true;
        }
        
        if(this.externalSource){
            await this.getItemsFromExternalSource();
        }
        else{        
            let max = (this.maxItemsDisplayed > 0 && this.itemsToDisplay.length > (this.maxItemsDisplayed + this.indexItem) ? (this.maxItemsDisplayed + this.indexItem) : this.itemsToDisplay.length);       
            if(this.indexItem < max){
                let cpt = this.indexItem;
                for(let i=this.indexItem;i<this.itemsToDisplay.length;i++){
                    let item = this.itemsToDisplay[i];
                    let itemSelected = this.itemsSelected.get(item.value);
                    if(!itemSelected || !this.multiple || (itemSelected && this.multiple && this.displayItemsSelected)){
                        this.itemsDisplayed.push(item);
                        cpt++;
                        if(cpt >= max){
                            break;
                        }
                    }                
                }                 
                this.indexItem = max;
            }
            else{
                this.disabledInfiniteScroll = true;
            }
        }
        
        if(init){
            this.isFetching = false;
        }
    }

    async searchList(search: any) {
        this.searchValue = search;
        if(!this.externalSource){
            const query = search.toLowerCase();        
            if(query != ''){
                this.itemsToDisplay = this.items.filter((e) => e.label.toLowerCase().indexOf(query) > -1);
            }
            else{
                this.itemsToDisplay = this.items;
            }        
        }
        this.indexItem = 0;
        this.disabledInfiniteScroll = false;
        this.itemsDisplayed = [];
        await this.setItemsDisplayed(true);
    }
    
    getItemsSelected(): Map<number|string|boolean, MarvListItem>{
        return this.itemsSelectedCopy;
    }
       
    selectItem(item: MarvListItem){     
        let itemSelected = this.itemsSelectedCopy.get(item.value);   
        if(itemSelected){
            this.itemsSelectedCopy.delete(item.value);
        }
        else{
            if(!this.multiple){               
                this.itemsSelectedCopy.clear();
            }         
            this.itemsSelectedCopy.set(item.value, item);
        }         
        this.onSelectItem.emit(this.getItemsSelected());
    }    
    
    selectAll() {
        let itemsSelected: Map<number|string|boolean, MarvListItem> = new Map();
        for(let item of this.itemsToDisplay){
            itemsSelected.set(item.value, item);
        }
        this.itemsSelectedCopy = itemsSelected;      
        this.allSelected = true; 
        this.onSelectItem.emit(this.getItemsSelected());       
    }
    
   async unselectAll() {   
        this.itemsSelectedCopy.clear();          
        this.allSelected = false;
        this.onSelectItem.emit(this.getItemsSelected());
    }
       
    async onIonInfinite(event: any){
        await this.setItemsDisplayed();
        event.target.complete();
    }
}
